import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  Handle,
  Position,
  MarkerType,
} from '@xyflow/react';
import type { Node, Edge, NodeTypes } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useCallback, useMemo } from 'react';
import type { FlowNode as ApiFlowNode } from '@/lib/operator-api/flows-client';

// ─── Types ──────────────────────────────────────────────────────

interface FlowCanvasProps {
  businessType?: string;
  locale: string;
  readOnly?: boolean;
  showControls?: boolean;
  showMiniMap?: boolean;
  onNodeClick?: (nodeId: string) => void;
  onBadgeClick?: (nodeId: string) => void;
  conversationCounts?: Record<string, number>;
  /** API flow nodes — when provided, renders these instead of the hardcoded booking flow */
  flowNodes?: ApiFlowNode[];
}

type FlowNodeType = 'start' | 'interaction' | 'decision' | 'action' | 'terminal';

interface FlowNodeDef {
  id: string;
  label: string;
  type: FlowNodeType;
  templateSlug?: string | null;
}

interface FlowEdgeDef {
  source: string;
  target: string;
  label?: string;
}

// ─── Dynamic graph derivation from API nodes ─────────────────────

/**
 * Derive nodes, edges, and positions from the API's FlowNode array.
 * - Nodes are sorted by `position` to determine order
 * - Edges are generated linearly along the main path
 * - Decision nodes get a "Yes"/"No" branch when extra terminal nodes exist
 */
function buildFlowGraph(
  apiNodes: ApiFlowNode[],
  locale: string,
): { nodeDefs: FlowNodeDef[]; edgeDefs: FlowEdgeDef[]; positions: Record<string, { x: number; y: number }> } {
  const sorted = [...apiNodes].sort((a, b) => a.position - b.position);

  // Build node definitions
  const nodeDefs: FlowNodeDef[] = sorted.map((n) => ({
    id: n.slug,
    label: locale === 'pt-BR' ? (n.namePtBr || n.name) : n.name,
    type: (n.nodeType as FlowNodeType) || 'interaction',
    templateSlug: n.templateSlug || null,
  }));

  // Separate main path (up to and including first terminal) from branch terminals
  const firstTerminalIdx = sorted.findIndex((n) => n.nodeType === 'terminal');
  const mainPath = firstTerminalIdx >= 0 ? sorted.slice(0, firstTerminalIdx + 1) : sorted;
  const branchTerminals = firstTerminalIdx >= 0
    ? sorted.slice(firstTerminalIdx + 1).filter((n) => n.nodeType === 'terminal')
    : [];

  // Linear edges along main path
  const edgeDefs: FlowEdgeDef[] = [];
  for (let i = 0; i < mainPath.length - 1; i++) {
    edgeDefs.push({ source: mainPath[i].slug, target: mainPath[i + 1].slug });
  }

  // Branch edges from decision node(s) to extra terminals
  if (branchTerminals.length > 0) {
    const lastDecision = [...mainPath].reverse().find((n) => n.nodeType === 'decision');
    if (lastDecision) {
      // Label the existing edge from the decision as "Yes"
      const yesEdge = edgeDefs.find((e) => e.source === lastDecision.slug);
      if (yesEdge) yesEdge.label = 'Yes';
      // Add "No" edges to branch terminals
      for (const bt of branchTerminals) {
        edgeDefs.push({ source: lastDecision.slug, target: bt.slug, label: 'No' });
      }
    }
  }

  // Calculate positions
  const MAIN_X = 250;
  const BRANCH_X = 500;
  const Y_STEP = 100;
  const Y_GAP_AFTER_DECISION = 20; // extra gap after decision for visual clarity
  const positions: Record<string, { x: number; y: number }> = {};
  let y = 0;

  for (let i = 0; i < mainPath.length; i++) {
    // Add extra gap after a decision node
    if (i > 0 && mainPath[i - 1].nodeType === 'decision') {
      y += Y_GAP_AFTER_DECISION;
    }
    positions[mainPath[i].slug] = { x: MAIN_X, y };
    y += Y_STEP;
  }

  // Branch terminals — offset right, aligned with the node after the decision
  for (let i = 0; i < branchTerminals.length; i++) {
    const lastDecision = [...mainPath].reverse().find((n) => n.nodeType === 'decision');
    const decisionPos = lastDecision ? positions[lastDecision.slug] : { x: MAIN_X, y: 0 };
    positions[branchTerminals[i].slug] = {
      x: BRANCH_X,
      y: decisionPos.y + Y_STEP + Y_GAP_AFTER_DECISION + i * Y_STEP,
    };
  }

  return { nodeDefs, edgeDefs, positions };
}

// ─── Fallback booking flow (used when no API nodes provided) ─────

const FALLBACK_BOOKING_NODES: FlowNodeDef[] = [
  { id: 'greeting', label: 'Greeting', type: 'start' },
  { id: 'service_select', label: 'Service Selection', type: 'interaction' },
  { id: 'date_select', label: 'Date Selection', type: 'interaction' },
  { id: 'time_select', label: 'Time Selection', type: 'interaction' },
  { id: 'confirm', label: 'Confirmation', type: 'decision' },
  { id: 'payment', label: 'Payment', type: 'action' },
  { id: 'booked', label: 'Booking Confirmed', type: 'terminal' },
  { id: 'cancelled', label: 'Cancelled', type: 'terminal' },
];

const FALLBACK_POSITIONS: Record<string, { x: number; y: number }> = {
  greeting: { x: 250, y: 0 },
  service_select: { x: 250, y: 100 },
  date_select: { x: 250, y: 200 },
  time_select: { x: 250, y: 300 },
  confirm: { x: 250, y: 400 },
  payment: { x: 250, y: 520 },
  booked: { x: 250, y: 640 },
  cancelled: { x: 500, y: 520 },
};

const FALLBACK_EDGES: FlowEdgeDef[] = [
  { source: 'greeting', target: 'service_select' },
  { source: 'service_select', target: 'date_select' },
  { source: 'date_select', target: 'time_select' },
  { source: 'time_select', target: 'confirm' },
  { source: 'confirm', target: 'payment', label: 'Yes' },
  { source: 'confirm', target: 'cancelled', label: 'No' },
  { source: 'payment', target: 'booked' },
];

// ─── Node styling ───────────────────────────────────────────────

const NODE_STYLES: Record<FlowNodeType, { bg: string; border: string }> = {
  start: { bg: '#DBEAFE', border: '#60A5FA' },
  interaction: { bg: '#F0FDF4', border: '#4ADE80' },
  decision: { bg: '#FEFCE8', border: '#FACC15' },
  action: { bg: '#FAF5FF', border: '#C084FC' },
  terminal: { bg: '#F3F4F6', border: '#9CA3AF' },
};

const MINIMAP_COLORS: Record<FlowNodeType, string> = {
  start: '#60A5FA',
  interaction: '#4ADE80',
  decision: '#FACC15',
  action: '#C084FC',
  terminal: '#9CA3AF',
};

// ─── Custom Node Component ──────────────────────────────────────

function FlowStepNode({ id, data }: { id: string; data: Record<string, unknown> }) {
  const nodeType = (data.nodeType as FlowNodeType) || 'interaction';
  const style = NODE_STYLES[nodeType];
  const count = data.conversationCount as number | undefined;
  const templateSlug = data.templateSlug as string | null | undefined;
  const onNodeClick = data.onNodeClick as ((id: string) => void) | undefined;
  const onBadgeClick = data.onBadgeClick as ((id: string) => void) | undefined;

  return (
    <div
      className="relative cursor-pointer transition-shadow hover:shadow-lg"
      style={{
        background: style.bg,
        border: `2px solid ${style.border}`,
        borderRadius: '8px',
        padding: '10px 24px',
        minWidth: '180px',
        textAlign: 'center',
      }}
      onClick={() => onNodeClick?.(id)}
      data-testid={`flow-node-${id}`}
    >
      {nodeType !== 'start' && (
        <Handle
          type="target"
          position={Position.Top}
          style={{ background: style.border, width: 8, height: 8 }}
        />
      )}
      <span className="text-sm font-medium">{data.label as string}</span>
      {templateSlug && (
        <div className="text-[10px] text-gray-500 mt-0.5 font-mono truncate">
          Uses template: {templateSlug}
        </div>
      )}
      {count != null && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onBadgeClick?.(id);
          }}
          className="absolute -top-2.5 -right-2.5 bg-primary text-primary-foreground text-xs rounded-full w-6 h-6 flex items-center justify-center border-0 cursor-pointer hover:scale-110 transition-transform"
          data-testid={`flow-badge-${id}`}
        >
          {count}
        </button>
      )}
      {nodeType !== 'terminal' && (
        <Handle
          type="source"
          position={Position.Bottom}
          style={{ background: style.border, width: 8, height: 8 }}
        />
      )}
    </div>
  );
}

const nodeTypes: NodeTypes = {
  flowStep: FlowStepNode,
};

// ─── Main Canvas Component ──────────────────────────────────────

function FlowCanvasInner({
  locale,
  readOnly = true,
  showControls = true,
  showMiniMap = false,
  onNodeClick,
  onBadgeClick,
  conversationCounts,
  flowNodes: apiFlowNodes,
}: FlowCanvasProps) {
  // Build graph from API nodes or fall back to hardcoded booking flow
  const { nodeDefs, edgeDefs, positions } = useMemo(() => {
    if (apiFlowNodes && apiFlowNodes.length > 0) {
      return buildFlowGraph(apiFlowNodes, locale);
    }
    return {
      nodeDefs: FALLBACK_BOOKING_NODES,
      edgeDefs: FALLBACK_EDGES,
      positions: FALLBACK_POSITIONS,
    };
  }, [apiFlowNodes, locale]);

  const nodes: Node[] = useMemo(
    () =>
      nodeDefs.map((n) => ({
        id: n.id,
        type: 'flowStep',
        position: positions[n.id] || { x: 250, y: 0 },
        data: {
          label: n.label,
          nodeType: n.type,
          templateSlug: n.templateSlug || null,
          conversationCount: conversationCounts?.[n.id] ?? undefined,
          onNodeClick,
          onBadgeClick,
        },
        draggable: !readOnly,
        selectable: true,
      })),
    [nodeDefs, positions, conversationCounts, onNodeClick, onBadgeClick, readOnly],
  );

  const edges: Edge[] = useMemo(
    () =>
      edgeDefs.map((e) => ({
        id: `${e.source}-${e.target}`,
        source: e.source,
        target: e.target,
        type: 'smoothstep',
        animated: true,
        label: e.label,
        style: { stroke: '#94a3b8', strokeWidth: 2 },
        labelStyle: { fontSize: 11, fontWeight: 500, fill: '#64748b' },
        labelBgStyle: { fill: '#f8fafc', stroke: '#e2e8f0' },
        labelBgPadding: [6, 4] as [number, number],
        labelBgBorderRadius: 4,
        markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8', width: 16, height: 16 },
      })),
    [edgeDefs],
  );

  const miniMapColor = useCallback(
    (node: Node) => MINIMAP_COLORS[(node.data?.nodeType as FlowNodeType) || 'interaction'],
    [],
  );

  return (
    <div className="w-full h-full" data-testid="flow-canvas">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.3 }}
        nodesDraggable={!readOnly}
        nodesConnectable={false}
        elementsSelectable={!readOnly}
        panOnDrag
        zoomOnScroll
        minZoom={0.3}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#e2e8f0" />
        {showControls && <Controls showInteractive={false} />}
        {showMiniMap && <MiniMap nodeColor={miniMapColor} pannable zoomable />}
      </ReactFlow>
    </div>
  );
}

// Wrap in provider so ReactFlow context is available
export function FlowCanvas(props: FlowCanvasProps) {
  return (
    <ReactFlowProvider>
      <FlowCanvasInner {...props} />
    </ReactFlowProvider>
  );
}
